import { inject, Pipe, PipeTransform } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { TocInfo } from "@orion2/models/couch.models";
import {
  AdditionalDocs,
  Bookmark,
  HistoryElem,
  Inspection,
  Navigation
} from "@orion2/models/tocitem.models";
import { convertRevLabel, getDefaultAuthor } from "@orion2/utils/functions.utils";
import { ConfService, Store } from "@viewer/core";
import { SearchResult } from "@viewer/core/search/searchModel";
import { TocInfoWithAnchor } from "@viewer/shared-module/multilinks/multilinks.service";
import {
  OrionCardData,
  OrionCardFooter
} from "@viewer/shared-module/orion-card/orion-card.component";

type allTypes =
  | AdditionalDocs
  | Bookmark
  | HistoryElem
  | Inspection
  | Navigation
  | SearchResult
  | TocInfo
  | TocInfoWithAnchor;

export enum OrionCardSource {
  BOOKMARK = "bookmark",
  HISTORY = "history",
  INSPECTION = "inspection",
  LINK = "link",
  NAVIGATION = "navigation",
  SEARCH = "search",
  TOC = "toc",
  TOC_ITEM = "tocItem"
}

@Pipe({
  name: "orionCardFormatter",
  standalone: true
})
export class OrionCardPipe implements PipeTransform {
  private readonly confService = inject(ConfService);
  private readonly store = inject(Store);
  private readonly translate = inject(TranslateService);

  private sourcesAvailable = {
    bookmark: (element: Bookmark) => this.bookmark(element),
    history: (element: HistoryElem) => this.history(element),
    inspection: (element: Inspection) => this.inspection(element),
    link: (element: TocInfoWithAnchor) => this.link(element),
    navigation: (element: Navigation) => this.navigation(element),
    search: (element: SearchResult) => this.search(element),
    toc: (element: TocInfo) => this.toc(element),
    tocItem: (element: AdditionalDocs) => this.tocItem(element)
  };

  public transform(element: allTypes, source: string): OrionCardData {
    if (!this.sourcesAvailable[source]) {
      throw new Error(
        `Source "${source}" is not available in the source list for the OrionCard pipe.`
      );
    }

    return this.sourcesAvailable[source](element);
  }

  /**
   * Creates an Orion card for the Bookmark component.
   *
   * @param element The element with the data to create the Orion card.
   * @returns an Orion card.
   */
  private bookmark(element: Bookmark): OrionCardData {
    return {
      dmc: element.dmc,
      title: element.title,
      forceTitle: true,
      subtitle: element.reference,
      revisionMark: element.revision,
      versions: element.versions,
      applicabilityMD5: element.applicabilityMD5,
      footer: this.createFooter(element)
    };
  }

  /**
   * Creates an Orion card for the HistoryList component.
   *
   * @param element The element with the data to create the Orion card.
   * @returns an Orion card.
   */
  private history(element: HistoryElem): OrionCardData {
    return {
      dmc: element.dmc,
      title: element.dmc,
      forceVisibility: true
    };
  }

  /**
   * Creates an Orion card for the Inspection component.
   *
   * @param element The element with the data to create the Orion card.
   * @returns an Orion card.
   */
  private inspection(element: Inspection): OrionCardData {
    return {
      title: element.title,
      subtitle: `${element.tasks.length} ${this.translate.instant("inspection.task")}`,
      footer: this.createFooter(element)
    };
  }

  /**
   * Creates an Orion card for a link (Multilinks component).
   *
   * @param element The element with the data to create the Orion card.
   * @returns an Orion card.
   */
  private link(element: TocInfoWithAnchor): OrionCardData {
    const metadata = element.metadata;
    const additionalRef = element.anchor?.startsWith("csn")
      ? ` - ${element.anchor.split("-").pop()}`
      : "";
    const subtitle = metadata.reference + additionalRef;

    return {
      dmc: metadata.dmc,
      title: metadata.shortTitle,
      subtitle,
      forceSubTitle: true,
      revisionMark: metadata.revision,
      versions: metadata.versions,
      applicabilityMD5: metadata.applicabilityMD5,
      showAdditionalInfos: true
    };
  }

  /**
   * Creates an Orion card for a navigation (NavigationTree component).
   *
   * @param element The element with the data to create the Orion card.
   * @returns an Orion card.
   */
  private navigation(element: Navigation): OrionCardData {
    return {
      dmc: element.dmc,
      title: element.dmc,
      forceVisibility: true,
      showAdditionalInfos: true
    };
  }

  /**
   * Creates an Orion card for the SearchResult component.
   *
   * @param element The element with the data to create the Orion card
   * @returns an Orion card.
   */
  private search(element: SearchResult): OrionCardData {
    element["author"] = getDefaultAuthor(this.confService.isOnPrem);
    const footer = element.type ? this.createFooter(element) : undefined;
    return {
      dmc: element.dmc,
      title: element.shortTitle,
      subtitle: element.reference,
      versions: element.versions,
      applicabilityMD5: element.applicabilityMD5,
      showAdditionalInfos: true,
      issueNumber: element.issueNumber,
      issueDate: element.issueDate,
      footer
    };
  }

  /**
   * Creates an Orion card for the Toc component.
   *
   * @param element The element with the data to create the Orion card.
   * @returns an Orion card.
   */
  private toc(element: TocInfo): OrionCardData {
    return {
      dmc: element.dmc,
      title: element["type"] ? element.title : element.shortTitle,
      subtitle: element.reference,
      revisionMark: element["type"] || element.revision,
      versions: element.versions,
      applicabilityMD5: element.applicabilityMD5,
      showAdditionalInfos: true,
      issueNumber: element["issueNumber"],
      issueDate: element["issueDate"]
    };
  }

  /**
   * Creates an Orion card for the TocItemList component.
   *
   * @param element The element with the data to create the Orion card.
   * @returns an Orion card.
   */
  private tocItem(element: AdditionalDocs): OrionCardData {
    const revisionMark = element["isArchived"] ? "archivedsb" : element.type;
    element.author = getDefaultAuthor(this.confService.isOnPrem, element.author);
    return {
      title: element.title,
      subtitle: element.reference,
      revisionMark,
      issueNumber: element["issueNumber"],
      issueDate: element["issueDate"],
      footer: this.createFooter(element)
    };
  }

  /**
   * Creates the footer for the Orion card.
   *
   * @param element The element with the data to create the footer.
   * @param author The author of the card.
   * @returns the footer of the Orion card.
   */
  private createFooter(
    element: AdditionalDocs | Bookmark | Inspection | SearchResult
  ): OrionCardFooter {
    return {
      key: "tocItem.createdBy",
      parameters: {
        author: element["author"],
        date: new Date(element.date).toLocaleString(this.store.currentLanguage),
        rev: convertRevLabel(element.minRevision)
      }
    };
  }
}
